import React, { useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
    styled,
    Box,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    CircularProgress,
    TextField,
    // wrap
} from '@mui/material';
import { DragHandle, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useSelectUsers } from '../../../../main/apps/FunctionalApps/schedule-list-view/Schedule.queries';
import useIsMobile from '../../../../hooks/useIsMobile';
import { prorateAmount } from '../utils';

const DraggableWrap = styled(Box)(() => ({
    backgroundColor: '#FAFAFA',
    borderRadius: 5,
    maxHeight: 470,
    overflow: 'scroll',
    display: 'flex',
    justifyContent: 'center',
}));

const DraggableListItem = styled(ListItem)(() => ({
    backgroundColor: '#FFFFFF',
    borderRadius: 5,
    display: 'flex',
    alignItems: 'center',
    border: '1px solid rgba(0, 0, 0, 0.12)',
}));

const isArraySameOrder = (a, b) => {
    return a.every((item, index) => item === b[index]);
};

const ProducerPickupSequence = ({ name, reference_name, resource, props, findRef, autoProrate, inputProps }) => {
    const {
        watch,
        setValue,
        control,
        register,
        selectedValues,
        getValues,
        trigger,
        formState: { defaultValues },
    } = useFormContext();
    const [
        _emptyGroupsResponse,
        { data, isLoading },
        // wrap
    ] = useSelectUsers({ queryParams: props.queryParams, userGroupsEnabled: false, enabled: true });
    const { fields, move, replace } = useFieldArray({ control, name });
    const isMobile = useIsMobile();
    const referenceUsers = watch(reference_name);
    const fieldWatcher = watch(name);
    const referenced = watch(findRef?.referenceField);
    const prorateReferenceAmount = watch(autoProrate?.totalAmountField);
    const hasUpdated = useRef(false);

    // Generates array field when reference users changes or producer data is loaded
    useEffect(() => {
        if (!referenceUsers?.length) {
            replace([]);
            return;
        }
        if (!data?.length) return;

        const validReferenceUsers = referenceUsers.filter((id) => data.some((producer) => producer._id === id));
        const producers = validReferenceUsers.map((id) => {
            const existingField = fieldWatcher?.find((field) => field?.producer_id === id) ?? defaultValues[name]?.find((field) => field?.producer_id === id);
            const matchedProducer = data.find((producer) => producer._id === id);

            return {
                producer_id: id,
                name: existingField?.name ?? matchedProducer?.name ?? '',
                expected_amount: existingField?.expected_amount ?? 0,
            };
        });

        replace(producers ?? []);
    }, [referenceUsers, data]);

    // Update values if findRef has data
    useEffect(() => {
        const referenceFieldValue = getValues(findRef?.referenceField);
        const referenceFieldDefaultValue = defaultValues?.[findRef?.referenceField];
        const reference = selectedValues?.[findRef?.referenceField];
        const referenceValue = reference?.[findRef?.referenceKey];

        // Ignore all updates when alwaysOverwrite has been enabled and the referenceField hasn't been changed
        if (!hasUpdated.current && referenceFieldValue === referenceFieldDefaultValue) {
            return;
        }

        if (referenced && referenceValue) {
            const producerPickupSequence = referenceValue.map((p) => {
                const existingField = (fieldWatcher ?? defaultValues[name])?.find((field) => field?.producer_id === p.id);
                return { ...p, name: existingField?.name ?? data?.find((producer) => producer._id === p.id)?.name };
            });
            replace(producerPickupSequence);
            trigger(name);
        }

        hasUpdated.current = true;
    }, [referenced]);

    useEffect(() => {
        if (autoProrate?.totalAmountField && prorateReferenceAmount && fields?.length) {
            const producerSequenceSum = fields.reduce((acc, field) => acc + (field.expected_amount ?? 0), 0);
            // If autoprorate is enabled and the amount is not equal to the producer sequence sum, prorate the sequence
            if (producerSequenceSum !== prorateReferenceAmount) {
                const proratedSequence = prorateAmount(prorateReferenceAmount, fields);
                replace(proratedSequence);
                trigger(name);
            }
            // Trigger validation for the total amount field
            trigger(autoProrate?.totalAmountField);
        }
    }, [prorateReferenceAmount, fields]);

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        move(result.source.index, result.destination.index);
    };

    const moveItem = (index, direction) => {
        move(index, index + direction);
    };

    if (isLoading) {
        return <CircularProgress />;
    }

    return (
        <>
            <Box display="flex" alignItems="center" p={2}>
                <Typography variant="h5">{props.label}</Typography>
            </Box>
            <Box sx={{ display: 'flex', px: 2 }}>
                <Typography
                    variant="body1"
                    fontWeight={600}
                    component="div"
                    sx={{ flex: 2 }}
                    //
                >
                    Order
                </Typography>
                <Typography
                    variant="body1"
                    fontWeight={600}
                    component="div"
                    sx={{ flex: 4 }}
                    //
                >
                    {resource}
                </Typography>
                <Typography
                    variant="body1"
                    fontWeight={600}
                    component="div"
                    sx={{ flex: 4 }}
                    //
                >
                    Amount
                </Typography>
            </Box>
            <DraggableWrap>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="droppable-list">
                        {(droppable) => (
                            <List
                                {...droppable.droppableProps}
                                sx={{
                                    width: '100%',
                                    px: isMobile ? 0 : 2,
                                    ...(isMobile && {
                                        '& .MuiListItemText-primary': {
                                            fontSize: '1.4rem',
                                        },
                                    }),
                                }}
                                ref={droppable.innerRef}
                            >
                                {fields.map((field, index) => (
                                    <Draggable key={field.id} draggableId={field.id} index={index}>
                                        {(draggable) => (
                                            <DraggableListItem
                                                ref={draggable.innerRef}
                                                {...draggable.draggableProps}
                                                {...draggable.dragHandleProps}
                                                //
                                            >
                                                <Typography variant={isMobile ? 'body2' : 'body1'} component="div" sx={{ flex: 1 }}>
                                                    {index + 1}
                                                </Typography>
                                                <ListItemIcon sx={{ ...(isMobile && { minWidth: 32 }) }}>
                                                    <DragHandle />
                                                </ListItemIcon>
                                                <ListItemText primary={field.name} sx={{ flex: isMobile ? 6 : 4 }} />
                                                <TextField type="number" inputProps={{ ...inputProps }} {...register(`${name}.${index}.expected_amount`)} sx={{ flex: 3 }} size={isMobile ? 'small' : 'medium'} />
                                                <IconButton disabled={index === 0} onClick={() => moveItem(index, -1)} size={isMobile ? 'small' : 'medium'}>
                                                    <ArrowUpward />
                                                </IconButton>
                                                <IconButton disabled={index === fields.length - 1} onClick={() => moveItem(index, 1)} size={isMobile ? 'small' : 'medium'}>
                                                    <ArrowDownward />
                                                </IconButton>
                                            </DraggableListItem>
                                        )}
                                    </Draggable>
                                ))}
                                {droppable.placeholder}
                            </List>
                        )}
                    </Droppable>
                </DragDropContext>
            </DraggableWrap>
        </>
    );
};

export default ProducerPickupSequence;
